import { FC, useMemo } from 'react'
import PageNextIcon from '@rsuite/icons/PageNext'
import PagePreviousIcon from '@rsuite/icons/PagePrevious'
import { FlexboxGrid, IconButton } from 'rsuite'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'
import Link from 'next/link'

import { QueryParam, ScreenSize } from '../../../utils/constants'
import useIsOnMobile from '../../../services/useIsOnMobile'
import useUrls from '../../../services/useUrls'

import styles from '../../../styles/PageSelector.module.less'

interface PageSelectorProps {
  amountOfPages: number
  currentPage: number
  handleClick: (page: number) => void
}

const PAGE_RANGE_OFFSET = 4
const SEPARATOR = '...'

const PageSelector: FC<PageSelectorProps> = (props) => {
  const {
    amountOfPages,
    currentPage,
    handleClick,
  } = props

  const { asPath, query } = useRouter()
  const { urlT } = useUrls()

  const isOnManagePage = asPath.includes('manage')
  const [baseUrlWithHash] = asPath.split('?')
  const [baseUrl] = baseUrlWithHash.split('#')
  const params = omit(query, [QueryParam.filters, QueryParam.page, QueryParam.supplierCode])
  const paramsStringFormat = new URLSearchParams(params as Record<string, string>).toString()

  const isOnMobile = useIsOnMobile(ScreenSize.sm)

  // Generate an array of pages based on the pages amount
  const fullPagesArray = Array.from({ length: amountOfPages }, (_, i) => i + 1)

  const displayPageList = useMemo(() => {
    const firstPage = 1
    const lastPage = amountOfPages

    // When there are fewer pages than the current pages range, show the whole array
    if (amountOfPages <= PAGE_RANGE_OFFSET) {
      return fullPagesArray
    }
    // When the current page is within the first pages offset range, show the first page offset,
    // add a separator and the last page
    if (currentPage < PAGE_RANGE_OFFSET) {
      return [...fullPagesArray.slice(0, PAGE_RANGE_OFFSET), SEPARATOR, lastPage]
    }
    // When the current page is outside the range of the total pages number, show the first page,
    // a separator and the last couple of pages based on the offset
    if (currentPage + (PAGE_RANGE_OFFSET - 1) > amountOfPages) {
      return [
        firstPage,
        SEPARATOR,
        ...fullPagesArray.slice(amountOfPages - PAGE_RANGE_OFFSET, lastPage),
      ]
    }
    // Otherwise, when a mid-page selected (greater than the offset, less than the last
    // page + offset), show the first page, a separator, the selected page + one page back +
    // one page forward, and the last page
    return [
      firstPage,
      SEPARATOR,
      ...fullPagesArray.slice(currentPage - 2, currentPage + 1),
      SEPARATOR,
      lastPage,
    ]
  }, [fullPagesArray.length, currentPage, amountOfPages])

  const renderPageListItem = () => displayPageList.map((pageListItem, i) => {
    const isSeparator = typeof pageListItem === 'string'
    const isActive = !isSeparator && (pageListItem === currentPage)
    const isFirstPage = pageListItem === 1
    const pageQueryParam = `page=${pageListItem}`

    const getPaginationParam = () => {
      if (paramsStringFormat) {
        return isFirstPage ? `?${paramsStringFormat}` : `?${paramsStringFormat}&${pageQueryParam}`
      }
      return isFirstPage ? '' : `?${pageQueryParam}`
    }

    const pageUrl = `${baseUrl}${getPaginationParam()}`
    const translatedUrl = isOnManagePage ? pageUrl : urlT(pageUrl)

    return (
      <FlexboxGrid.Item
        key={!isSeparator ? pageListItem : `${pageListItem}_${i}`}
        as={!isSeparator ? Link : undefined}
        className={`
            ${isActive ? styles['active-page'] : ''}
            ${!isSeparator ? 'margin-left-spacer margin-right-spacer' : styles.separator}
        `}
        shallow={!isSeparator ? true : undefined}
        scroll={!isSeparator ? true : undefined}
        href={translatedUrl}
        onClick={isSeparator ? undefined : () => handleClick(pageListItem)}
        data-testid={`${!isSeparator ? `page_${pageListItem}` : 'separator'} page-item`}
      >
        {pageListItem}
      </FlexboxGrid.Item>
    )
  })

  return (
    <FlexboxGrid
      justify="center"
      align="middle"
      className={styles['page-selector-container']}
      data-testid="page-selector-container"
    >
      <FlexboxGrid.Item>
        <IconButton
          appearance="subtle"
          disabled={currentPage === 1}
          icon={<PagePreviousIcon />}
          onClick={() => handleClick(currentPage - 1)}
          data-testid="prev-button"
          className={`margin-right-spacer-half ${styles['arrow-icon']}`}
          size={isOnMobile ? 'lg' : undefined}
        />
      </FlexboxGrid.Item>
      {renderPageListItem()}
      <FlexboxGrid.Item>
        <IconButton
          appearance="subtle"
          disabled={currentPage === amountOfPages}
          icon={<PageNextIcon />}
          onClick={() => handleClick(currentPage + 1)}
          data-testid="next-button"
          className={`margin-left-spacer-half ${styles['arrow-icon']}`}
          size={isOnMobile ? 'lg' : undefined}
        />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  )
}

export default PageSelector
